import { periodContaining, recentPeriods, type PeriodGrain } from '@oxyflows/domain';
import { useI18n } from '../i18n';

export interface Period {
  from: string;
  to: string;
  /** How it was chosen, so a report can label itself honestly. */
  grain: PeriodGrain | 'custom';
  key: string;
}

/**
 * Choosing what a report covers.
 *
 * Presets and an explicit range, together — because the two are asked for at
 * different moments. "This quarter" is what someone wants ninety per cent of
 * the time and should take one click; "the 3rd to the 19th, because that is
 * when the insurer's period ran" is the other ten, and a report that cannot
 * express it is a report someone rebuilds in a spreadsheet.
 *
 * The presets are computed by the same function the server uses, so "this
 * quarter" means the same range in the picker as in the figures. Typing a date
 * switches the grain to `custom`, which stops a report claiming to be a
 * quarter when it is eleven days.
 */
export function PeriodPicker({
  value,
  onChange,
  grains = ['month', 'quarter', 'half', 'year'],
}: {
  value: Period;
  onChange: (next: Period) => void;
  grains?: PeriodGrain[];
}) {
  const { t, formatDate } = useI18n();

  const options = value.grain === 'custom' ? [] : recentPeriods(value.grain, 6);

  return (
    <>
      <div className="segmented" role="group" aria-label={t.reports.title}>
        {grains.map((g) => (
          <button
            key={g}
            type="button"
            aria-pressed={value.grain === g}
            onClick={() => {
              const p = periodContaining(g);
              onChange({ from: p.from, to: p.to, grain: g, key: p.key });
            }}
          >
            {(t.reports.grain as Record<string, string>)[g]}
          </button>
        ))}
        <button
          type="button"
          aria-pressed={value.grain === 'custom'}
          onClick={() => onChange({ ...value, grain: 'custom', key: 'custom' })}
        >
          {t.reports.custom}
        </button>
      </div>

      {value.grain === 'custom' ? (
        <div className="row" style={{ gap: 6 }}>
          <input
            type="date"
            className="select-inline ltr-value"
            value={value.from}
            max={value.to}
            onChange={(e) => onChange({ ...value, from: e.target.value, grain: 'custom', key: 'custom' })}
            aria-label={t.reports.from}
          />
          <span style={{ color: 'var(--ink-3)' }}>–</span>
          <input
            type="date"
            className="select-inline ltr-value"
            value={value.to}
            min={value.from}
            onChange={(e) => onChange({ ...value, to: e.target.value, grain: 'custom', key: 'custom' })}
            aria-label={t.reports.to}
          />
        </div>
      ) : (
        <select
          className="select-inline"
          value={value.key}
          onChange={(e) => {
            const chosen = options.find((p) => p.key === e.target.value);
            if (chosen) onChange({ from: chosen.from, to: chosen.to, grain: value.grain, key: chosen.key });
          }}
          aria-label={t.reports.title}
        >
          {options
            .slice()
            .reverse()
            .map((p) => (
              <option key={p.key} value={p.key}>
                {p.key}
              </option>
            ))}
        </select>
      )}

      <span style={{ color: 'var(--ink-3)', fontSize: 12.5, whiteSpace: 'nowrap' }}>
        {formatDate(`${value.from}T12:00:00`)} – {formatDate(`${value.to}T12:00:00`)}
      </span>
    </>
  );
}

/** The current period of a grain, as a starting value. */
export function initialPeriod(grain: PeriodGrain = 'quarter'): Period {
  const p = periodContaining(grain);
  return { from: p.from, to: p.to, grain, key: p.key };
}
